import { useState, useEffect } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { 
  Package, 
  Truck, 
  Clock, 
  CheckCircle,
  MapPin,
  Heart,
  Users,
  Navigation,
  Apple,
  Coffee,
  Cake,
  Gift
} from 'lucide-react'
import { useUser } from '../../contexts/UserContext'
import { getMyAcceptedDonations, Donation } from '../../api/donations'
import { completeOnboarding } from '../../api/profile'
import { isVolunteerIdApproved } from '../../utils/volunteerAccess'
import { communityImages } from '../../assets/images'
import VolunteerIdStatusBanner from '../VolunteerIdStatusBanner'

const VolunteerDashboard = () => {
  const { user, isAuthenticated } = useUser()
  const navigate = useNavigate()
  const [myDeliveries, setMyDeliveries] = useState<Donation[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [showSkipOnboarding, setShowSkipOnboarding] = useState(false)
  const [isSkippingOnboarding, setIsSkippingOnboarding] = useState(false)

  // Check if onboarding is needed - only for new volunteer sign-ups, not logins
  useEffect(() => {
    // Check if user just registered (via sessionStorage flag set during registration)
    const justRegistered = sessionStorage.getItem('justRegistered') === 'true'
    
    if (user && user.userType === 'VOLUNTEER' && !user.onboardingCompleted && justRegistered) {
      // Only trigger onboarding for new volunteer sign-ups, not logins
      // Clear the flag so it doesn't trigger on subsequent page loads
      sessionStorage.removeItem('justRegistered')
      navigate('/onboarding')
    } else {
      setShowSkipOnboarding(false)
    }
  }, [user, navigate])

  // Check if user just completed onboarding (check URL params)
  const [searchParams] = useSearchParams()
  const justCompletedOnboarding = searchParams.get('onboarding') === 'completed'
  const idApproved = isVolunteerIdApproved(user)

  useEffect(() => {
    const fetchDeliveries = async () => {
      if (!isAuthenticated) {
        setIsLoading(false)
        return
      }

      try {
        const accepted = await getMyAcceptedDonations()
        // Active first, then by soonest scheduled/pickup time
        const sorted = [...accepted].sort((a, b) => {
          const active = (d: Donation) =>
            d.status === 'ACCEPTED' || d.status === 'PICKED_UP' ? 0 : 1
          const byStatus = active(a) - active(b)
          if (byStatus !== 0) return byStatus
          const ta = new Date(a.scheduledDate || a.pickupTime || a.createdAt).getTime()
          const tb = new Date(b.scheduledDate || b.pickupTime || b.createdAt).getTime()
          return ta - tb
        })
        setMyDeliveries(sorted)
      } catch (error) {
        console.error('Error fetching deliveries:', error)
      } finally {
        setIsLoading(false)
      }
    }

    fetchDeliveries()
  }, [isAuthenticated])

  const handleSkipOnboarding = async () => {
    setIsSkippingOnboarding(true)
    try {
      const updatedProfile = await completeOnboarding()
      if (updatedProfile) {
        window.location.reload()
      }
    } catch (error) {
      console.error('Error skipping onboarding:', error)
    } finally {
      setIsSkippingOnboarding(false)
    }
  }

  const formatStatus = (status: string) =>
    status
      .split('_')
      .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
      .join(' ')

  const formatDeliveryTime = (delivery: Donation) => {
    const raw = delivery.scheduledDate || delivery.pickupTime
    if (!raw) return 'Time not scheduled'
    return new Date(raw).toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    })
  }

  const formatAddress = (delivery: Donation) => {
    if (delivery.pickupStreet) {
      return `${delivery.pickupStreet}, ${delivery.pickupCity}, ${delivery.pickupState}`
    }
    return delivery.pickupAddress || 'Address not available'
  }

  const stats = [
    {
      title: 'Active Deliveries',
      value: myDeliveries.filter(d => d.status === 'ACCEPTED' || d.status === 'PICKED_UP').length.toString(),
      change: 'In progress',
      icon: Truck,
      color: 'text-blue-600'
    }
  ]

  const urgencyColors = {
    LOW: 'bg-green-100 text-green-800',
    MEDIUM: 'bg-yellow-100 text-yellow-800',
    HIGH: 'bg-orange-100 text-orange-800',
    URGENT: 'bg-red-100 text-red-800'
  }

  const statusColors: Record<string, string> = {
    NEW: 'bg-blue-100 text-blue-800',
    ACCEPTED: 'bg-purple-100 text-purple-800',
    PICKED_UP: 'bg-yellow-100 text-yellow-800',
    DELIVERED: 'bg-green-100 text-green-800',
    CANCELLED: 'bg-gray-100 text-gray-800',
    EXPIRED: 'bg-red-100 text-red-800'
  }

  return (
    <div className="min-h-screen bg-gray-50 relative overflow-hidden">
      {/* Skip Onboarding Banner */}
      {showSkipOnboarding && (
        <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-blue-700">
                  <strong>Welcome back!</strong> Since you're an existing user, you can skip the onboarding process and go straight to your dashboard.
                </p>
              </div>
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => navigate('/onboarding')}
                className="text-sm text-blue-600 hover:text-blue-800 underline"
              >
                Complete Onboarding
              </button>
              <button
                onClick={handleSkipOnboarding}
                disabled={isSkippingOnboarding}
                className="bg-blue-600 text-white px-4 py-2 rounded-md text-sm hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSkippingOnboarding ? 'Skipping...' : 'Skip Onboarding'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Food Decorations */}
      <div className="fixed inset-0 pointer-events-none opacity-5 z-0">
        <Apple className="absolute top-24 left-16 h-6 w-6 text-red-500 animate-pulse" />
        <Coffee className="absolute top-48 right-20 h-8 w-8 text-amber-600 animate-bounce" />
        <Cake className="absolute bottom-36 left-24 h-8 w-8 text-orange-400 animate-pulse" />
        <Gift className="absolute bottom-24 right-40 h-4 w-4 text-green-500 animate-bounce" />
      </div>

      {/* Welcome Banner for New Volunteers */}
      {justCompletedOnboarding && (
        <div className="bg-green-50 border-l-4 border-green-400 p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <CheckCircle className="h-5 w-5 text-green-400" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-green-700">
                <strong>Welcome to HarvestHelp!</strong> Your profile is set up. An admin still needs to approve
                your photo ID before you can claim deliveries.
              </p>
            </div>
          </div>
        </div>
      )}

      {!idApproved && user?.userType === 'VOLUNTEER' && (
        <div className="mb-6">
          <VolunteerIdStatusBanner />
        </div>
      )}

      {/* Header */}
      <div className="bg-gradient-to-r from-accent-50 to-primary-50 border-b border-gray-200 py-4 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center">
              <div className="relative mr-3">
                <Heart className="h-8 w-8 text-accent-600" />
                <Apple className="absolute -top-1 -right-1 h-3 w-3 text-red-500" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900 flex items-center">
                  Welcome, {user?.firstName || 'Volunteer'}!
                  <Gift className="ml-2 h-5 w-5 text-green-500" />
                </h1>
              <p className="text-gray-600 mt-1">
                Ready to help? Here are deliveries waiting for amazing volunteers like you
              </p>
              </div>
            </div>
            <Link
              to="/deliveries"
              className="btn-primary inline-flex items-center"
            >
              <Navigation className="h-4 w-4 mr-2" />
              Browse Deliveries
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Hero Section with Image */}
        <div className="bg-gradient-to-r from-accent-50 to-primary-50 rounded-xl p-6 mb-8 relative overflow-hidden">
          <div className="absolute inset-0 opacity-15">
            <img
              src={communityImages.communityGathering6}
              alt=""
              className="w-full h-full object-cover"
              aria-hidden="true"
            />
          </div>
          <div className="relative z-10 flex flex-col lg:flex-row items-center gap-6">
            <div className="flex-1 text-center lg:text-left">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">You're Making a Difference!</h2>
              <p className="text-gray-600 mb-4">
                Your volunteer work is helping to reduce food waste and feed families in need.
                Keep up the amazing work!
              </p>
            </div>
            <div className="relative">
              <img
                src={communityImages.communityGathering2}
                alt="Community members donating food together"
                className="w-48 h-32 object-cover rounded-lg shadow-lg"
              />
              <div className="absolute -top-2 -right-2 bg-white rounded-full p-2 shadow-lg">
                <Users className="h-5 w-5 text-accent-500" />
              </div>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {stats.map((stat, index) => (
            <div key={index} className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{stat.value}</p>
                  <p className="text-sm text-gray-500 mt-1">{stat.change}</p>
                </div>
                <div className={`p-3 rounded-full bg-gray-50 ${stat.color}`}>
                  <stat.icon className="h-6 w-6" />
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* My Deliveries */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="p-6 border-b border-gray-200">
                <div className="flex justify-between items-center">
                  <h2 className="text-lg font-semibold text-gray-900">
                    My Deliveries
                  </h2>
                  <Link to="/deliveries" className="text-primary-600 hover:text-primary-700 text-sm font-medium">
                    Find more
                  </Link>
                </div>
              </div>
              <div className="p-6">
                {isLoading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                    <p className="text-gray-500 mt-2">Loading your deliveries...</p>
                  </div>
                ) : myDeliveries.length === 0 ? (
                  <div className="text-center py-8">
                    <Truck className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500 mb-2">You don’t have any deliveries yet</p>
                    <p className="text-sm text-gray-400 mb-4">
                      Claim a pickup from Find Deliveries and it will show up here with its time.
                    </p>
                    <Link to="/deliveries" className="text-primary-600 hover:text-primary-700 text-sm font-medium">
                      Browse deliveries
                    </Link>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {myDeliveries.map((delivery) => (
                      <div
                        key={delivery.id}
                        className="border border-gray-200 rounded-lg p-4 hover:border-primary-300 transition-colors"
                      >
                        <div className="flex justify-between items-start mb-3 gap-3">
                          <h3 className="font-medium text-gray-900">{delivery.title}</h3>
                          <div className="flex flex-wrap gap-2 justify-end">
                            <span
                              className={`px-2 py-1 rounded-full text-xs font-medium ${
                                statusColors[delivery.status] || 'bg-gray-100 text-gray-800'
                              }`}
                            >
                              {formatStatus(delivery.status)}
                            </span>
                            {delivery.urgency && (
                              <span
                                className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  urgencyColors[delivery.urgency as keyof typeof urgencyColors]
                                }`}
                              >
                                {delivery.urgency}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="space-y-2 text-sm text-gray-600 mb-3">
                          <div className="flex items-center">
                            <Clock className="h-4 w-4 mr-2 text-gray-400 shrink-0" />
                            <span>{formatDeliveryTime(delivery)}</span>
                          </div>
                          <div className="flex items-start">
                            <MapPin className="h-4 w-4 mr-2 text-gray-400 mt-0.5 shrink-0" />
                            <span>{formatAddress(delivery)}</span>
                          </div>
                          {delivery.estimatedWeight && (
                            <div className="flex items-center">
                              <Package className="h-4 w-4 mr-2 text-gray-400 shrink-0" />
                              <span>{delivery.estimatedWeight}</span>
                            </div>
                          )}
                        </div>
                        <div className="flex justify-end">
                          <Link
                            to={`/delivery/${delivery.id}`}
                            className="text-primary-600 hover:text-primary-700 font-medium text-sm"
                          >
                            View Details
                          </Link>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
              <div className="space-y-3">
                <Link
                  to="/deliveries"
                  className="flex items-center p-3 rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-primary-50 transition-colors"
                >
                  <Navigation className="h-5 w-5 text-primary-600 mr-3" />
                  <span className="font-medium text-gray-900">Find Deliveries</span>
                </Link>
                <Link
                  to="/food-banks"
                  className="flex items-center p-3 rounded-lg border border-gray-200 hover:border-secondary-300 hover:bg-secondary-50 transition-colors"
                >
                  <MapPin className="h-5 w-5 text-secondary-600 mr-3" />
                  <span className="font-medium text-gray-900">Nearby Food Banks</span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default VolunteerDashboard